import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Carousel } from "antd";
import { Link } from "react-router-dom";
import { ArrowDownOutlined, CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import GetPerformanceStatsApi from "../../api/performance/GetPerformanceStatsApi";
import { SimpleLoader } from "../sub/Loaders";
import Theme from "../../theme/Theme";

function getProgressColor(value) {
  const pct = Math.min(Math.max(value || 0, 0), 100);
  if (pct < 40) return "#ef4444";
  if (pct < 70) return "#eab308";
  return "#22c55e";
}

function CombinedPerformanceCarousel() {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOrgPerf, setShowOrgPerf] = useState(true);
  const carouselRef = React.useRef();
  
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    // Read widget preference for organization performance
    try {
      const saved = localStorage.getItem("widgetPreferences");
      if (saved) {
        const prefs = JSON.parse(saved);
        if (prefs.organizationPerformance === false) setShowOrgPerf(false);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    getPerformanceStats();
  }, [perfCycleState]);

  async function getPerformanceStats() {
    setLoading(true);
    const apiResponse = await GetPerformanceStatsApi(
      positionState?.id,
      perfCycleState?.id
    );

    if (apiResponse.success) {
      setPerformanceData(apiResponse?.data);
      setLoading(false);
    }
  }

  // Mini bar chart component
  const MiniBarChart = ({ value }) => {
    const totalBars = 12;
    const perf = Math.min(Math.max(value || 0, 0), 100);
    const filledBars = Math.round((perf / 100) * totalBars);
    const barColor = getProgressColor(value);
    
    return (
      <div className="flex items-end gap-1 mt-4" style={{ height: "28px" }}>
        {Array.from({ length: totalBars }).map((_, i) => {
          const heights = [14, 10, 18, 12, 22, 16, 26, 14, 20, 28, 18, 24];
          return (
            <div
              key={i}
              style={{
                width: "7px",
                height: `${heights[i]}px`,
                borderRadius: "3px",
                backgroundColor: i < filledBars ? barColor : "#e5e7eb",
              }}
            />
          );
        })}
      </div>
    );
  };

  const PerformanceCard = ({ title, percentage, chartData, linkTo }) => {
    const onTrack = chartData?.[1]?.value || 0;
    const offTrack = chartData?.[2]?.value || 0;
    const atRisk = chartData?.[0]?.value || 0;
    const totalPlans = onTrack + offTrack + atRisk;
    const onTrackPercent = totalPlans > 0 ? Math.round((onTrack / totalPlans) * 100) : 0;
    const perfColor = getProgressColor(percentage);

    return (
      <div className="px-1">
        <div
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: "white",
            border: `1px solid ${Theme().navBorder}`,
          }}
        >
          <div className="grid grid-cols-4">
            {/* Performance Percentage */}
            <div
              className="p-5"
              style={{ borderRight: `1px solid ${Theme().navBorder}` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  {title}
                </span>
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: `${perfColor}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: perfColor,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    {parseFloat(percentage || 0) >= 40 ? (
                      <>
                        <path d="M1 11 L4 8 L7 9 L11 4" stroke={perfColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        <path d="M8 4 L11 4 L11 7" stroke={perfColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </>
                    ) : (
                      <>
                        <path d="M1 3 L4 6 L7 5 L11 10" stroke={perfColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        <path d="M8 10 L11 10 L11 7" stroke={perfColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </>
                    )}
                  </svg>
                </span>
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1.1,
                }}
              >
                {parseFloat(percentage || 0).toFixed(2)}%
              </div>
              <MiniBarChart value={percentage} />
            </div>

            {/* Plans On Track */}
            <div
              className="p-5"
              style={{ borderRight: `1px solid ${Theme().navBorder}` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Plans On Track
                </span>
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "#22c55e",
                  }}
                >
                  <CheckCircleOutlined />
                </span>
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1.1,
                }}
              >
                {onTrack}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontFamily: "Inter, sans-serif",
                  marginTop: "6px",
                }}
              >
                {onTrackPercent}% of strategic portfolio
              </div>
              <Link
                to={linkTo}
                className="flex items-center gap-1 mt-3"
                style={{
                  fontSize: "12px",
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                }}
              >
                View on-track plans <span>→</span>
              </Link>
            </div>

            {/* Plans Off Track */}
            <div
              className="p-5"
              style={{ borderRight: `1px solid ${Theme().navBorder}` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Plans Off Track
                </span>
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "#ef4444",
                  }}
                >
                  <ArrowDownOutlined />
                </span>
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1.1,
                }}
              >
                {offTrack}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontFamily: "Inter, sans-serif",
                  marginTop: "6px",
                }}
              >
                Requires immediate remedial action
              </div>
              <Link
                to={linkTo}
                className="flex items-center gap-1 mt-3"
                style={{
                  fontSize: "12px",
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                }}
              >
                View off-track plans <span>→</span>
              </Link>
            </div>

            {/* Attention Required */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Attention Required
                </span>
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(234, 179, 8, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "#eab308",
                  }}
                >
                  <WarningOutlined />
                </span>
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1.1,
                }}
              >
                {atRisk}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontFamily: "Inter, sans-serif",
                  marginTop: "6px",
                }}
              >
                Milestones and alignment stalled
              </div>
              <Link
                to={linkTo}
                className="flex items-center gap-1 mt-3"
                style={{
                  fontSize: "12px",
                  color: Theme().textPrimary,
                  fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                }}
              >
                View at-risk plans <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mb-6">
        <SimpleLoader />
      </div>
    );
  }

  return (
    <div className="mb-6" style={{ position: "relative" }}>
      <style>{carouselStyles}</style>
      <Carousel
        ref={carouselRef}
        autoplay
        autoplaySpeed={7000}
        dots={true}
        dotPosition="bottom"
        style={{ paddingBottom: "30px" }}
      >
        {/* Unit Performance */}
        <div>
          <PerformanceCard
            title="Unit Performance"
            percentage={performanceData?.unit?.performance}
            chartData={performanceData?.unit?.data}
            linkTo="/top-plans"
          />
        </div>

        {/* Organization Performance */}
        {showOrgPerf && (
          <div>
            <PerformanceCard
              title="Organization Performance"
              percentage={performanceData?.organization?.performance}
              chartData={performanceData?.organization?.data}
              linkTo="/top-plans"
            />
          </div>
        )}

        {/* Your Performance */}
        <div>
          <PerformanceCard
            title="Your Performance"
            percentage={performanceData?.individual?.performance}
            chartData={performanceData?.individual?.data}
            linkTo={`/positions-performance/${performanceData?.individual?.positionId}`}
          />
        </div>
      </Carousel>
    </div>
  );
}

export default CombinedPerformanceCarousel;

const carouselStyles = `
  .ant-carousel .slick-dots li button {
    background: rgba(241, 90, 36, 0.38) !important;
    width: 8px !important;
    height: 8px !important;
    border-radius: 50% !important;
  }
  
  .ant-carousel .slick-dots li.slick-active button {
    background: #F15A24 !important;
    width: 10px !important;
    height: 10px !important;
  }
`;
